import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  User, 
  Calendar,
  MapPin,
  CheckCircle,
  XCircle,
  Camera,
  Image as ImageIcon,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import ProfileVerificationActions from './ProfileVerificationActions';
import SelfieImageZoom from './SelfieImageZoom';
import ProfilePhotosGallery from './ProfilePhotosGallery';
import AdminAvatarImage from './AdminAvatarImage';
import AdminPhotoImage from './AdminPhotoImage';

interface ProfileVerificationCardProps {
  verification: {
    id: string;
    user_id: string;
    username: string;
    user_photo_url?: string;
    status: string;
    submitted_at: string;
    reviewed_at?: string;
    reviewer_username?: string;
    selfie_photo_url?: string;
    email_verified: boolean;
    email_verified_at?: string;
    rejection_reason?: string;
    ai_analysis?: any;
    bio?: string;
    birth_date?: string;
    gender?: string;
    city?: string;
    state_code?: string;
    main_photo_url?: string;
    profile_photos?: string[];
  };
  onAction: (action: 'approve' | 'reject' | 'reset', id: string, reason?: string) => Promise<boolean>;
}

const ProfileVerificationCard: React.FC<ProfileVerificationCardProps> = ({
  verification,
  onAction
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSelfieZoom, setShowSelfieZoom] = useState(false);
  const [showPhotosGallery, setShowPhotosGallery] = useState(false);
  const [expandBio, setExpandBio] = useState(false);

  const handleAction = async (action: 'approve' | 'reject' | 'reset', id: string, reason?: string) => {
    setIsProcessing(true);
    try {
      const success = await onAction(action, id, reason);
      if (success) {
        const actionLabels = {
          approve: 'aprovada',
          reject: 'rejeitada',
          reset: 'resetada'
        };
        toast.success(`Verificação ${actionLabels[action]} com sucesso`);
      }
      return success;
    } catch (error) {
      const actionLabels = {
        approve: 'aprovar',
        reject: 'rejeitar',
        reset: 'resetar'
      };
      toast.error(`Erro ao ${actionLabels[action]} verificação`);
      return false;
    } finally {
      setIsProcessing(false);
    }
  };

  const calculateAge = (birthDate?: string) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const getGenderLabel = (gender?: string) => {
    const labels: Record<string, string> = {
      male: 'Masculino',
      female: 'Feminino',
      other: 'Outro'
    };
    return gender ? labels[gender] || gender : 'Não informado';
  };

  const age = calculateAge(verification.birth_date);
  const location = [verification.city, verification.state_code].filter(Boolean).join(', ');
  const profilePhotos = verification.profile_photos || [];
  const bioText = verification.bio || 'Sem bio';
  const shouldTruncateBio = bioText.length > 150;
  const displayBio = expandBio || !shouldTruncateBio ? bioText : `${bioText.slice(0, 150)}...`;

  return (
    <Card className="bg-white/5 border-white/10 hover:bg-white/10 transition-all">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3 flex-1">
            <AdminAvatarImage
              src={verification.main_photo_url}
              fallback={verification.username.charAt(0).toUpperCase()}
              className="h-12 w-12 border-2 border-luxury-gold/30"
              fallbackClassName="bg-luxury-gold/20 text-luxury-gold"
            />
            <div className="flex-1">
              <h3 className="text-white font-semibold text-lg">@{verification.username}</h3>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1">
                <div className="flex items-center gap-1 text-xs text-white/60">
                  <Calendar className="h-3 w-3" />
                  {new Date(verification.submitted_at).toLocaleDateString('pt-BR')}
                </div>
                {location && (
                  <div className="flex items-center gap-1 text-xs text-white/60">
                    <MapPin className="h-3 w-3" />
                    {location}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Email & Selfie Verification Status */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Email Status */}
          <div className={`rounded-lg p-3 border ${
            verification.email_verified 
              ? 'bg-green-500/10 border-green-500/30' 
              : 'bg-red-500/10 border-red-500/30'
          }`}>
            <div className="flex items-center gap-2">
              {verification.email_verified ? (
                <CheckCircle className="h-4 w-4 text-green-400" />
              ) : (
                <XCircle className="h-4 w-4 text-red-400" />
              )}
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  verification.email_verified ? 'text-green-300' : 'text-red-300'
                }`}>
                  E-mail {verification.email_verified ? 'Verificado' : 'Não Verificado'}
                </p>
                {verification.email_verified && verification.email_verified_at && (
                  <p className="text-xs text-white/60 mt-0.5">
                    {new Date(verification.email_verified_at).toLocaleDateString('pt-BR')}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Selfie Status */}
          <div className={`rounded-lg p-3 border ${
            verification.selfie_photo_url 
              ? 'bg-green-500/10 border-green-500/30' 
              : 'bg-red-500/10 border-red-500/30'
          }`}>
            <div className="flex items-center gap-2">
              {verification.selfie_photo_url ? (
                <CheckCircle className="h-4 w-4 text-green-400" />
              ) : (
                <XCircle className="h-4 w-4 text-red-400" />
              )}
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  verification.selfie_photo_url ? 'text-green-300' : 'text-red-300'
                }`}>
                  Selfie {verification.selfie_photo_url ? 'Enviada' : 'Não Enviada'}
                </p>
                {verification.selfie_photo_url && verification.ai_analysis && (
                  <p className="text-xs text-white/60 mt-0.5">
                    IA: {Math.round(verification.ai_analysis.confidence * 100)}% confiança
                  </p>
                )}
              </div>
              {verification.selfie_photo_url && (
                <button
                  onClick={() => setShowSelfieZoom(true)}
                  className="text-luxury-gold hover:text-luxury-gold/80 transition-colors"
                >
                  <Camera className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Selfie Preview (se existe) */}
        {verification.selfie_photo_url && (
          <div 
            onClick={() => setShowSelfieZoom(true)}
            className="relative group cursor-pointer rounded-lg overflow-hidden border border-white/20 hover:border-luxury-gold/50 transition-all"
          >
            <img
              src={verification.selfie_photo_url}
              alt="Selfie de verificação"
              className="w-full h-32 object-cover"
            />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center">
              <Camera className="h-6 w-6 text-white" />
            </div>
            <Badge className="absolute top-2 left-2 bg-black/70 text-white border-white/20">
              Selfie de Verificação
            </Badge>
          </div>
        )}

        {/* Profile Info */}
        <div className="border-t border-white/10 pt-4 space-y-3">
          <h4 className="text-white font-medium flex items-center gap-2">
            <User className="h-4 w-4" />
            Informações do Perfil
          </h4>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-2 text-sm">
            {age && (
              <div>
                <span className="text-white/60">Idade:</span>
                <span className="text-white ml-2">{age} anos</span>
              </div>
            )}
            <div>
              <span className="text-white/60">Gênero:</span>
              <span className="text-white ml-2">{getGenderLabel(verification.gender)}</span>
            </div>
          </div>

          {/* Bio */}
          <div>
            <p className="text-white/60 text-xs mb-1">Bio:</p>
            <p className="text-white/90 text-sm leading-relaxed">{displayBio}</p>
            {shouldTruncateBio && (
              <button
                onClick={() => setExpandBio(!expandBio)}
                className="text-luxury-gold text-xs mt-1 hover:underline flex items-center gap-1"
              >
                {expandBio ? 'Ver menos' : 'Ver mais'}
                <ChevronRight className={`h-3 w-3 transition-transform ${expandBio ? 'rotate-90' : ''}`} />
              </button>
            )}
          </div>

          {/* Profile Photos */}
          {profilePhotos.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-white/60 text-xs flex items-center gap-1">
                  <ImageIcon className="h-3 w-3" />
                  Fotos do Perfil ({profilePhotos.length})
                </p>
                <button
                  onClick={() => setShowPhotosGallery(true)}
                  className="text-luxury-gold text-xs hover:underline"
                >
                  Ver todas
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {profilePhotos.slice(0, 6).map((photo, index) => (
                  <div
                    key={index}
                    onClick={() => setShowPhotosGallery(true)}
                    className="relative aspect-square rounded overflow-hidden cursor-pointer group"
                  >
                    <AdminPhotoImage
                      src={photo}
                      alt={`Foto ${index + 1}`}
                      context="thumbnail"
                      className="w-full h-full object-cover transition-transform group-hover:scale-110"
                    />
                    {index === 5 && profilePhotos.length > 6 && (
                      <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                        <span className="text-white font-medium">+{profilePhotos.length - 6}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <ProfileVerificationActions
          verification={verification}
          onAction={handleAction}
          isProcessing={isProcessing}
        />
      </CardContent>

      {/* Modals */}
      {verification.selfie_photo_url && (
        <SelfieImageZoom
          isOpen={showSelfieZoom}
          onClose={() => setShowSelfieZoom(false)}
          imageUrl={verification.selfie_photo_url}
          username={verification.username}
          aiAnalysis={verification.ai_analysis}
        />
      )}

      {profilePhotos.length > 0 && (
        <ProfilePhotosGallery
          isOpen={showPhotosGallery}
          onClose={() => setShowPhotosGallery(false)}
          photos={profilePhotos}
          username={verification.username}
        />
      )}
    </Card>
  );
};

export default ProfileVerificationCard;
